import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";

const title = "Elever i behov av extra stöd | Swedish International School";
const description =
  "Så arbetar Swedish International School Estepona med elever i behov av särskilt stöd. Bifoga relevant information vid ansökan – varje fall prövas individuellt.";

export const Route = createFileRoute("/extra-stod")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/extra-stod") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/extra-stod"),
  }),
  component: ExtraStod,
});

function ExtraStod() {
  return (
    <>
      <PageHero
        eyebrow="Antagning"
        title="Elever i behov av extra stöd"
        intro="Swedish International School Estepona (Marbella) strävar efter att vara en inkluderande skola. Inför varje skolstart bedömer vi öppet om våra resurser kan möta elevens behov på ett tryggt och hållbart sätt."
      />

      <Prose>
        <p>
          Det är en liten skola med begränsade resurser och lokaler, vilket begränsar i vilken
          utsträckning elever med behov av särskilt stöd kan få sina behov tillgodosedda. Vid ansökan
          är det därför viktigt att bifoga all relevant information.
        </p>
        <p>
          Behov av extra material, hjälpmedel och eventuell extra lärarresurs bekostas av
          vårdnadshavare. Antagningen av elev med behov av extra stöd prövas av ledningen och
          diskuteras med målsman i varje enskilt fall.
        </p>
        <p>
          Tveka inte att höra av dig med frågor till{" "}
          <a href="mailto:info@sweintschool.com">info@sweintschool.com</a>.
        </p>

        <h2>Så går bedömningen till</h2>
        <p>
          När en ansökan innehåller uppgifter om tidigare stöd, utredningar eller diagnoser läser
          ledningen igenom underlaget tillsammans med berörd personal. Vi tittar på vilket stöd
          eleven har haft, vad som fungerat bra och vad som krävs i vår miljö. Därefter bokas ett
          samtal med vårdnadshavare där vi går igenom vad skolan realistiskt kan erbjuda och var
          gränserna går.
        </p>
        <p>
          Vi vill vara tydliga hellre än att lova för mycket. Som liten skola har vi inte alla de
          resurser en större skolorganisation har, och därför kan svaret ibland bli att vi inte kan
          tillgodose behovet på ett bra sätt. Ett tidigt och ärligt samtal är bättre för barnet än en
          skolstart som inte håller.
        </p>

        <h2>Vad vi behöver från er</h2>
        <ul>
          <li>Pedagogiska kartläggningar, åtgärdsprogram eller motsvarande från tidigare skola.</li>
          <li>Eventuella utredningar eller intyg som är relevanta för skolgången.</li>
          <li>Er egen beskrivning av vad som fungerar för barnet i skolmiljö.</li>
          <li>Uppgift om hjälpmedel eller anpassningar som används idag.</li>
        </ul>

        <h2>Uppföljning under skolgången</h2>
        <p>
          Behov förändras över tid. Klasslärare och{" "}
          <a className="text-primary link-quiet" href="/elevhalsoteamet">elevhälsoteamet</a>{" "}
          följer upp anpassningar löpande och stämmer av med hemmet vid utvecklingssamtal eller
          oftare när det behövs. Upplever ni att något inte fungerar hör ni av er direkt till
          klassläraren – det är alltid rätt väg in.
        </p>
      </Prose>

      <FaqBlock items={faqFor("extra-stod", "sv")} locale={"sv"} />

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
